//
import React, { useEffect, useRef, useState, useReducer, useContext, createContext, forwardRef } from 'react';
import { useIsMobileLayout } from '@/hooks';

// Components
import { Flex, SignPos, CommentInput } from '@/components';
import SignatureLoader from '../SignatureLoader/SignatureLoader';

// Externals
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { FixedSizeList } from 'react-window';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

import { PDFDocument } from 'pdf-lib';

// Redux
import { useDispatch, useSelector } from 'react-redux';

import { useLocation } from 'react-router-dom';

// Actions
import {
  setPage,
  setNumPages,
  addPdfDoc,
  setDimessions,
  addSignaturePosition,
  setScrollToPage
} from '@/redux/slices/document';

import { setComments, addComment } from '@/redux/slices/commentSlice';

// Constants
import { statusDocument, PDFJS_SAFE_OPTIONS } from '@/constants';

// Sections
import Sign from './Sign';

// Externals
import axios from 'axios';
import Cookies from 'js-cookie';

// Helpers
import { moreParticipants } from '@/helpers';
import { 
  setSignatureLinePositioned, 
  setSignaturePositionMode, 
  updateSignaturePositionId, 
  removeSignaturePositionById, 
  removeSignaturePosition,
  updatePositionParticipant,
  removePositionById,
  updatePositionId
} from '../../redux/slices/document';

// Constants
import { typesSign, getSignatureGeometry, getSignatureFootprint, getPositionFromFootprintOrigin } from '@/constants';

// Tipo de firma con el que se estampará la posición de un participante (el dueño usa el del documento).
const getParticipantTypeOfSign = (participant, participants, documentInfo, ownerEmail) => {
  if (participant?.email && participant.email === ownerEmail) return documentInfo?.typeOfSignOwner;
  const full = participants?.find(p => p.email === participant?.email) || participant;
  return full?.typeofSign;
};

// connections
import { getCommentsApi, postCommentApi } from '@/connections';
import toast from 'react-hot-toast';

// Hooks
import { useMakeRequest } from '@/hooks';

import { Tooltip } from 'react-tooltip';
import AnimatedMessageCircleMoreIcon from '../../assets/AnimatedMessageCircleMoreIcon';
import { TbArrowsMove } from 'react-icons/tb';
import { HiOutlineTrash, HiCheck, HiX } from 'react-icons/hi';
import { createPortal } from 'react-dom';

const options = {
  ...PDFJS_SAFE_OPTIONS,
  cMapUrl: 'cmaps/',
  cMapPacked: true,
  standardFontDataUrl: 'standard_fonts/'
};

// Create Context for Attach Button
const AttachContext = createContext({ showButton: false, onAttach: () => {}, isMobile: false });


// Caja pendiente de confirmar (solo mobile). Se dibuja en una capa fija sobre TODA la pantalla (portal a
// <body>), no dentro de la hoja. En un iPhone la caja, aunque existía en el DOM y con estilos visibles, no se
// pintaba cuando estaba sobre la primera hoja (dentro del visor con scroll, el zoom y el canvas); la capa fija
// sí se pinta (igual que el aviso de instrucciones). Su posición sigue a la hoja en pantalla cada cuadro, y
// el arrastre convierte los píxeles de pantalla a unidades PDF con la escala real de la hoja.
const PendingSignatureOverlay = ({ pendingPosition, setPendingPosition, geometry, documentInfo, label, onCancel, onConfirm }) => {
  const [rect, setRect] = useState(null);
  const dragRef = useRef(null);

  useEffect(() => {
    let raf;
    const tick = () => {
      const el = document.querySelector(`[data-page-container="true"][data-page-index="${pendingPosition.page}"]`);
      if (el) {
        const r = el.getBoundingClientRect();
        setRect((prev) => (
          prev && Math.abs(prev.left - r.left) < 0.5 && Math.abs(prev.top - r.top) < 0.5 &&
          Math.abs(prev.width - r.width) < 0.5 && Math.abs(prev.height - r.height) < 0.5
            ? prev
            : { left: r.left, top: r.top, width: r.width, height: r.height }
        ));
      }
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(raf);
  }, [pendingPosition.page]);

  if (!rect || !documentInfo?.width || !documentInfo?.height || rect.width === 0) return null;

  const scaleX = rect.width / documentInfo.width;
  const scaleY = rect.height / documentInfo.height;
  const left = rect.left + (pendingPosition.x + geometry.offsetX) * scaleX;
  const top = rect.top + (pendingPosition.y + geometry.offsetY) * scaleY;

  const onPointerDown = (e) => {
    if (e.target.closest('.pending-cancel-btn, .pending-confirm-btn')) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.setPointerCapture) e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = {
      px: e.clientX,
      py: e.clientY,
      left0: pendingPosition.x + geometry.offsetX,
      top0: pendingPosition.y + geometry.offsetY
    };
  };
  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    // La caja es la huella de la firma: se convierte a la posición que se guarda (y se ajusta a la hoja).
    const { x, y } = getPositionFromFootprintOrigin(
      d.left0 + (e.clientX - d.px) / scaleX,
      d.top0 + (e.clientY - d.py) / scaleY,
      geometry,
      documentInfo.width,
      documentInfo.height
    );
    setPendingPosition((prev) => (prev ? { ...prev, x, y } : prev));
  };
  const onPointerEnd = (e) => {
    dragRef.current = null;
    if (e.currentTarget.releasePointerCapture) {
      try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (err) { /* ya liberado */ }
    }
  };

  return createPortal(
    <div
      className="signature-pending-box"
      style={{
        position: 'fixed',
        left: `${left}px`,
        top: `${top}px`,
        width: `${geometry.width * scaleX}px`,
        height: `${geometry.height * scaleY}px`,
        zIndex: 9000,
        touchAction: 'none'
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerEnd}
      onPointerCancel={onPointerEnd}
    >
      <button
        className="pending-cancel-btn"
        onTouchEnd={(e) => { e.stopPropagation(); e.preventDefault(); onCancel(); }}
        onClick={(e) => { e.stopPropagation(); onCancel(); }}
      >
        <HiX size={16} />
      </button>
      <button
        className="pending-confirm-btn"
        onTouchEnd={(e) => { e.stopPropagation(); e.preventDefault(); onConfirm(); }}
        onClick={(e) => { e.stopPropagation(); onConfirm(); }}
      >
        <HiCheck size={16} />
      </button>
      <span className="pending-name">{label}</span>
    </div>,
    document.body
  );
};

// Cajas guardadas (azules con el bote de basura), solo mobile. Mismo motivo que la caja pendiente: dentro de
// la hoja un iPhone no las pintaba, así que se dibujan en una capa fija (portal a <body>) recortada al área
// del visor, para que no tapen la cabecera ni el panel inferior al hacer scroll.
const SavedSignaturesOverlay = ({ positions, participants, documentInfo, userInfo, onDelete }) => {
  const [frame, setFrame] = useState(null);

  useEffect(() => {
    let raf;
    const same = (a, b) => a && b && Math.abs(a.left - b.left) < 0.5 && Math.abs(a.top - b.top) < 0.5 &&
      Math.abs(a.width - b.width) < 0.5 && Math.abs(a.height - b.height) < 0.5;
    const tick = () => {
      const pages = {};
      let clip = null;
      document.querySelectorAll('[data-page-container="true"]').forEach((el) => {
        const idx = el.getAttribute('data-page-index');
        // El visor principal es el que tiene scroll propio; las miniaturas no se usan en mobile.
        const r = el.getBoundingClientRect();
        pages[idx] = { left: r.left, top: r.top, width: r.width, height: r.height };
        if (!clip) {
          let n = el.parentElement;
          while (n && n !== document.body) {
            const oy = window.getComputedStyle(n).overflowY;
            if (oy === 'auto' || oy === 'scroll') { const c = n.getBoundingClientRect(); clip = { left: c.left, top: c.top, width: c.width, height: c.height }; break; }
            n = n.parentElement;
          }
        }
      });
      // El panel inferior (y su tirador, 40px arriba) va por encima del visor: la capa fija no debe tapar esa zona.
      const sheet = document.querySelector('[data-bottom-sheet="true"]');
      if (clip && sheet) {
        const limit = sheet.getBoundingClientRect().top - 40;
        const bottom = Math.min(clip.top + clip.height, limit);
        clip = { ...clip, height: Math.max(0, bottom - clip.top) };
      }
      const keys = Object.keys(pages);
      setFrame((prev) => (
        prev && prev.keys === keys.join(',') && same(prev.clip, clip) && keys.every((k) => same(prev.pages[k], pages[k]))
          ? prev
          : { keys: keys.join(','), pages, clip }
      ));
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(raf);
  }, []);

  if (!frame || !frame.clip || !documentInfo?.width || !documentInfo?.height) return null;

  const boxes = [];
  positions.forEach((pos, globalIndex) => {
    const pr = frame.pages[String(parseInt(pos.page, 10))];
    if (!pr || pr.width === 0) return;
    const participant = participants?.find(p => p.email === pos.email);
    const isOwner = pos.email === userInfo?.email;
    const footprint = getSignatureFootprint(
      pos,
      getSignatureGeometry(getParticipantTypeOfSign(participant || { email: pos.email }, participants, documentInfo, userInfo?.email))
    );
    const sx = pr.width / documentInfo.width;
    const sy = pr.height / documentInfo.height;
    boxes.push(
      <div
        key={`sig-pos-${pos.id ?? globalIndex}-${globalIndex}`}
        className="signature-box-container signature-position-box"
        style={{
          position: 'absolute',
          left: `${pr.left - frame.clip.left + footprint.left * sx}px`,
          top: `${pr.top - frame.clip.top + footprint.top * sy}px`,
          width: `${footprint.width * sx}px`,
          height: `${footprint.height * sy}px`,
          border: '2px dashed #285FF6',
          backgroundColor: 'rgba(74, 114, 178, 0.1)',
          pointerEvents: 'none',
          zIndex: 50,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <div className="signature-box-hover-content" style={{ pointerEvents: 'auto' }}>
          <div className="sig-info">
            <span className="sig-role">{isOwner ? 'Dueño' : 'Firmante'}</span>
            <span className="sig-name">{participant ? `${participant.name} ${participant.last_name || ''}` : pos.email}</span>
          </div>
          <button
            className="sig-delete-btn"
            style={{ pointerEvents: 'auto' }}
            onClick={(e) => { e.stopPropagation(); onDelete(pos, participant, isOwner, globalIndex); }}
          >
            <HiOutlineTrash size={14} />
          </button>
        </div>
      </div>
    );
  });

  return createPortal(
    <div
      style={{
        position: 'fixed',
        left: `${frame.clip.left}px`,
        top: `${frame.clip.top}px`,
        width: `${frame.clip.width}px`,
        height: `${frame.clip.height}px`,
        overflow: 'hidden',
        pointerEvents: 'none',
        zIndex: 8900
      }}
    >
      {boxes}
    </div>,
    document.body
  );
};

// InnerList component to render the list items and the attach button
const InnerList = forwardRef(({ children, style, ...rest }, ref) => {
  const { showButton, onAttach, isMobile, bottomPad = 0 } = useContext(AttachContext);
  
  // Aumentamos el espacio extra para asegurar que el botón se vea completo
  const buttonAreaHeight = 150; 
  const addedHeight = (showButton ? buttonAreaHeight : 0) + bottomPad;
  // Usamos parseFloat para mayor precisión y evitar recortes por redondeo
  const originalHeight = parseFloat(style.height) || 0;
  
  const modifiedStyle = {
    ...style,
    height: `${originalHeight + addedHeight}px`,
    position: 'relative'
  };

  return (
    <div ref={ref} style={modifiedStyle} {...rest}>
      {children}
      {showButton && (
        <div 
          style={{
            position: 'absolute',
            top: `${originalHeight}px`,
            left: 0,
            width: '100%',
            height: `${buttonAreaHeight}px`,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start', // Alineamos al inicio del área extra para que esté pegado al doc pero con espacio abajo
            paddingTop: '20px', // Un poco de aire desde el final del documento
            zIndex: 5
          }}
        >
          <button
            data-tooltip-id="attach-file-tooltip-inner"
            data-tooltip-content="Este botón permite adjuntar un archivo al documento. Se aceptan imágenes (JPG, PNG) y PDF, que se incorporarán como nuevas páginas."
            onClick={onAttach}
            style={{
              display: 'block',
              padding: '10px 20px',
              fontSize: '24px',
              border: 'none',
              borderRadius: '50%',
              backgroundColor: '#4A72B2',
              color: 'white',
              cursor: 'pointer',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
            }}
          >
            +
          </button>
          <Tooltip id="attach-file-tooltip-inner" place="top" effect="solid" />
        </div>
      )}
    </div>
  );
});

// arriba de todo
const MOBILE_BREAKPOINT = 991;
// Espacio al final de la lista en mobile: el tirador del panel inferior (40 px) queda encima del visor y taparía
// el final de la última hoja; con este margen la hoja sube por encima del tirador.
const MOBILE_BOTTOM_PAD = 56;
function useIsMobile () {
  const isMobile = useIsMobileLayout();
  return isMobile;
}

const Row = ({ index, style, data }) => {
  const {
    isFullScreen,
    isMobile,
    watch,
    mobileWidth,
    defaultWidth,
    mobileHeight,
    height,
    handleScroll,
    handleMaker,
    handlePageRenderSuccess,
    commentsByPage,
    inputState,
    setInputState,
    currentParticipant,
    userInfo,
    dispatch,
    addComment,
    postCommentApi,
    documentInfo,
    signaturePositions,
    participants,
    dimenssions,
    setRef,
    isMobileFull,
    pendingPosition,
    setPendingPosition,
    pendingNodeRef,
    saveCalculatedPosition,
    activePage,
    readOnly
  } = data;

  // Página actualmente mostrada en el visor principal (solo se pasa desde el
  // preview de miniaturas de /sign; en el resto de los usos de DocumentPdf
  // llega undefined y este resaltado simplemente no aplica).
  const isActivePage = activePage != null && Number(activePage) === index + 1;

  // Calculate width for the flex container to center content
  const flexWidth = isMobileFull ? '100vw' : (isFullScreen ? '100vw' : (watch && !isMobile ? 'calc(100vw - 610px)' : 'auto'));

  const scaleX = dimenssions?.width && documentInfo?.width ? dimenssions.width / documentInfo.width : 1;
  const scaleY = dimenssions?.height && documentInfo?.height ? dimenssions.height / documentInfo.height : 1;

  // Calculamos el factor de escala real visual (útil para móviles)
  const actualRenderedWidth = isMobileFull ? window.innerWidth : (isMobile ? mobileWidth : defaultWidth);
  const visualScaleX = actualRenderedWidth ? actualRenderedWidth / documentInfo?.width : scaleX;
  const visualScaleY = actualRenderedWidth && documentInfo?.height ? (actualRenderedWidth / (documentInfo.width / documentInfo.height)) / documentInfo.height : scaleY;

  return (
    <div style={style}>
      <Flex
        mt="0px"
        mb={watch ? '20px' : '24px'}
        key={index}
        justifyContent="center"
        px={isFullScreen ? 0 : (isMobile ? '1px' : 0)}
        w={flexWidth}
      >
        <div
          onClick={() => handleScroll(index)}
          ref={(el) => setRef(index, el)}
          style={{
            padding: !watch && '3px',
            background: 'transparent',
            margin: isFullScreen ? '0 auto' : (isMobile ? '0 0 0 10px' : 'auto'),
            width: isFullScreen ? '100%' : 'auto',
            display: isFullScreen ? 'flex' : 'block',
            justifyContent: isFullScreen ? 'center' : 'auto',
            height: '100%',
            border: isActivePage ? '2px solid #285FF6' : '2px solid transparent',
            borderRadius: isActivePage ? '6px' : 0,
            boxSizing: 'border-box',
            transition: 'border-color 0.15s ease-in'
          }}
        >
          <div 
            data-page-container="true"
            data-page-index={index + 1}
            style={{ position: 'relative', display: 'inline-block', lineHeight: 0 }}
          >
            {handleMaker(index)}
            <Page
              key={`page_${index + 1}`}
              width={actualRenderedWidth}
              height={isMobileFull ? undefined : (isMobile ? mobileHeight : (isFullScreen ? height : (watch ? height : undefined)))}
              renderAnnotationLayer={false}
              renderTextLayer={watch}
              pageNumber={index + 1}
              data-page-number={index + 1}
              onRenderSuccess={handlePageRenderSuccess}
              loading={null}
              noData={null}
              error={<div style={{ color: 'white', marginLeft: '20px' }}>Error al cargar la página.</div>}
            />
            {/* Render saved advanced signature positions (boxes) SOLO en la vista principal (watch) y ocultar si el doc ya fue enviado o completado o está en PENDING/PROCESSED */}
            {watch && !isMobile && !readOnly && documentInfo?.status !== statusDocument.SEND && documentInfo?.status !== statusDocument.COMPLETED && documentInfo?.status !== statusDocument.PENDING && documentInfo?.status !== statusDocument.PROCESSED && documentInfo?.status !== statusDocument.CANCELLED && signaturePositions && signaturePositions.filter(pos => parseInt(pos.page, 10) === index + 1).map((pos, idx) => {
               const participant = participants?.find(p => p.email === pos.email);
               const isOwner = pos.email === userInfo?.email;
               
               // Find the index in the global array to pass to delete function
               const globalIndex = signaturePositions.findIndex(p => p === pos);

               // En móviles usamos el visualScale para que las coordenadas se traduzcan exactamente al tamaño renderizado en pantalla
               const finalScaleX = isMobile ? visualScaleX : scaleX;
               const finalScaleY = isMobile ? visualScaleY : scaleY;
               // La caja se dibuja exactamente donde quedará la firma (ver constants/signatureGeometry.js).
               const footprint = getSignatureFootprint(
                 pos,
                 getSignatureGeometry(getParticipantTypeOfSign(participant || { email: pos.email }, participants, documentInfo, userInfo?.email))
               );

               return (
                   <div
                     key={`sig-pos-${idx}`}
                     className="signature-box-container signature-position-box"
                     style={{
                       position: 'absolute',
                       left: `${footprint.left * finalScaleX}px`,
                       top: `${footprint.top * finalScaleY}px`,
                       width: `${footprint.width * finalScaleX}px`,
                       height: `${footprint.height * finalScaleY}px`,
                       border: '2px dashed #285FF6',
                     backgroundColor: 'rgba(74, 114, 178, 0.1)',
                     pointerEvents: 'auto',
                     zIndex: 50,
                     display: 'flex',
                     alignItems: 'center',
                     justifyContent: 'center'
                   }}
                 >
                    <div className="signature-box-hover-content">
                      <div className="sig-info">
                        <span className="sig-role">{isOwner ? 'Dueño' : 'Firmante'}</span>
                        <span className="sig-name">{participant ? `${participant.name} ${participant.last_name || ''}` : pos.email}</span>
                      </div>
                      <button
                        className="sig-delete-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          dispatch(removeSignaturePositionById ? removeSignaturePositionById(pos.id) : removeSignaturePosition(globalIndex));
                          dispatch(removePositionById(pos.id)); // Sincronizar borrado con positions para firmas simples
                          if (pos.id) {
                            axios.post(`${process.env.REACT_APP_BASE_URL}signs/deletePosition`, {
                              id: pos.id,
                              signerId: participant?.id,
                              documentId: documentInfo?.id,
                              isOwner
                            }, { withCredentials: true }).catch(console.error);
                          }
                        }}
                      >
                        <HiOutlineTrash size={14} />
                      </button>
                    </div>
                 </div>
               );
            })}
            {(commentsByPage[index + 1] || []).map((c, idx) => (
              <React.Fragment key={`page-${index + 1}-comment-${c.id}-${idx}`}>
                <div
                  data-tooltip-id={`comment-tooltip-${index + 1}-${c.id}-${idx}`}
                  data-tooltip-content={`${c.author_email}: ${c.text}`}
                  style={{
                    position: 'absolute',
                    left: `${c.x_ratio * 100}%`,
                    top: `${c.y_ratio * 100}%`,
                    transform: 'translate(-50%, -50%)',
                    width: 16,
                    height: 16,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer'
                  }}
                >
                  <AnimatedMessageCircleMoreIcon bubbleColor="#FFD700" />
                </div>
                <Tooltip id={`comment-tooltip-${index + 1}-${c.id}-${idx}`} place="top" />
              </React.Fragment>
            ))}
            {inputState.visible && inputState.page === index + 1 && (
              <CommentInput
                key={`${inputState.page}-${inputState.x}-${inputState.y}`}
                visible={inputState.visible}
                x={inputState.x}
                y={inputState.y}
                onSave={text => {
                  const commenterEmail = currentParticipant?.email || userInfo.email;
                  postCommentApi(documentInfo.id, {
                    page: inputState.page,
                    xRatio: inputState.xRatio,
                    yRatio: inputState.yRatio,
                    text,
                    authorEmail: commenterEmail
                  }).then(res => {
                    dispatch(addComment({ page: inputState.page, comment: res.comment }));
                  });
                  setInputState(s => ({ ...s, visible: false }));
                }}
                onCancel={() => setInputState(s => ({ ...s, visible: false }))}
              />
            )}
          </div>
          {/* Número de página bajo la miniatura, solo en el sidebar de miniaturas
              (watch=false) — el visor principal no lo necesita. */}
          {!watch && (
            <div
              style={{
                textAlign: 'center',
                fontSize: '13px',
                lineHeight: '16px',
                marginTop: '6px',
                color: isActivePage ? '#285FF6' : '#6B7280',
                fontWeight: isActivePage ? 700 : 400
              }}
            >
              {index + 1}
            </div>
          )}
        </div>
      </Flex>
    </div>
  );
};

const DocumentPdf = ({
  height = 208,
  watch = false,
  handleScroll = () => {},
  isShared = false,
  isFullScreen = false,
  firstSignedPage = 0,
  positionsChecked = false,
  onDocumentLoadComplete = () => {},
  onPdfDocCreated,
  typeOfSign,
  triggerScrollAfterLoad,
  disableSignatureSelector = false,
  scrollContainerRef,
  enablePdfManipulation = true,
  cloudPDF: propCloudPDF,
  showAttachButton = false,
  onAttachFile = () => {},
  activePage = null,
  // Visor principal solo de lectura (vista previa de una plantilla antes de generarla): mismo tamaño y
  // seguimiento de página que watch, pero sin cajas ni controles de firma. Apagado por defecto.
  readOnly = false
}) => {
  const numPages = useSelector(s => s.document.numPages);
  const positions = useSelector(s => s.document.positions);
  const page = useSelector(s => s.document.page);
  const documentInfo = useSelector(s => s.document.documentInfo);
  const dimenssions = useSelector(s => s.document.dimenssions);
  const participants = useSelector(s => s.document.participants);
  const zoom = useSelector(s => s.document.zoom);
  const isSignaturePositionModeActive = useSelector(s => s.document.isSignaturePositionModeActive);
  const signaturePositions = useSelector(s => s.document.signaturePositions);
  const ignoreSignatureLines = useSelector(s => s.document.ignoreSignatureLines);
  const hasExistingSignatureLines = useSelector(s => s.document.hasExistingSignatureLines);
  const scrollTarget = useSelector(s => s.document.scrollToPage);
  const { primaryColor } = useSelector(({ theme }) => theme);
  const { email } = useSelector(({ userInfo }) => userInfo);
  const userInfo = useSelector(({ userInfo }) => userInfo);
  const currentParticipant = useSelector(state => state.document.currentParticipant);
  const location = useLocation();
  const cloudPDF = propCloudPDF || location?.state?.cloudPDF;
  const refDocs = useRef([]);
  const listRef = useRef(null);
  const containerRef = useRef(null);
  const justClosedIntro = useRef(false);
  const overlayDismissAtRef = useRef(0);
  const introShowCountRef = useRef(0);
  const hasScrolledToSignature = useRef(false);
  // Guardia independiente para el scroll de "ya firmé, confírmame mi página" (Signly/autógrafa
  // en /shared, disparado por triggerScrollAfterLoad tras el socket 'signatureAdded'). Antes
  // compartía hasScrolledToSignature con el scroll de "guía a dónde firmar" que corre al abrir
  // el documento (antes de firmar) — como ese siempre corre primero, dejaba el guardia en true
  // y el scroll de confirmación después de firmar nunca llegaba a ejecutarse.
  const hasScrolledAfterSign = useRef(false);
  const touchStateRef = useRef({
    startX: 0,
    startY: 0,
    moved: false,
    active: false,
    lastEndAt: 0
  });
  // console.log('refDocs:', refDocs);
  const dispatch = useDispatch();
  const commentsByPage = useSelector(s => s.comments.byPage);
  const [inputState, setInputState] = useState({
    visible: false,
    page: null,
    x: 0,
    y: 0,
    xRatio: 0,
    yRatio: 0
  });
  const [cursorPos, setCursorPos] = useState({ x: 0, y: 0, visible: false });
  const [pagesRendered, setPagesRendered] = useState(0);
  const [autoScrollDone, setAutoScrollDone] = useState(false);
  const [, forceUpdate] = useReducer(x => x + 1, 0);
  const isMobile = useIsMobile();
  const isMobileFull = isMobile && isFullScreen;
  // En mobile normal usamos 90%×80%, pero en fullscreen mobile queremos 100%
  const mobileWidth = isMobile
    ? Math.round(isFullScreen ? window.innerWidth : window.innerWidth * 0.9)
    : null;
  const mobileHeight = isMobile
    ? Math.round(isFullScreen ? window.innerHeight : window.innerHeight * 0.8)
    : null;
  const defaultWidth = isFullScreen ? undefined : (watch ? undefined : 180);
  const [showSelectionIntro, setShowSelectionIntro] = useState(false);
  const [pendingPosition, setPendingPosition] = useState(null); // { x, y, page } en unidades PDF, o null
  const pendingNodeRef = useRef(null);

  // Resetear estados internos cuando cambia el documento (cloudPDF)
  useEffect(() => {
    setPagesRendered(0);
    setAutoScrollDone(false);
    hasScrolledToSignature.current = false;
    hasScrolledAfterSign.current = false;
  }, [cloudPDF]);

  // Calcula la altura de cada página (aproximada, ajusta según zoom)
  const { width: docOriginalWidth, height: docOriginalHeight } = documentInfo || {};
  const aspectRatio = (docOriginalWidth && docOriginalHeight) ? docOriginalWidth / docOriginalHeight : 0.77; // 0.77 is approx Letter/A4 ratio

  // Calculate dynamic page height based on context (Mobile vs Desktop vs Sidebar)
  let calculatedPageHeight;
  if (isMobile) {
    // Mobile: Driven by width (90% or 100% of screen)
    calculatedPageHeight = mobileWidth / aspectRatio;
  } else if (watch) {
    // Desktop Main View: Driven by 'height' prop (zoom)
    calculatedPageHeight = height;
  } else {
    // Sidebar Preview: Driven by fixed width (180px)
    // When watch is false, Page uses defaultWidth=180 (implied)
    calculatedPageHeight = 180 / aspectRatio;
  }
  
  // El sidebar de miniaturas (watch=false) además del margen entre hojas (20px)
  // necesita espacio para el número de página que se dibuja debajo de cada una;
  // sin este espacio extra, el número quedaba recortado por la virtualización
  // (cada fila tiene una altura fija = pageItemHeight).
  const pageItemHeight = Math.round(calculatedPageHeight) + (watch ? 20 : 48);

  useEffect(() => {
    const handleScrollOrResize = () => forceUpdate();
    window.addEventListener('scroll', handleScrollOrResize, { passive: true });
    window.addEventListener('resize', handleScrollOrResize);
    return () => {
      window.removeEventListener('scroll', handleScrollOrResize);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, []);

  // ─── Cargar comentarios SOLO si es plantilla de KIUBIX ───
  useEffect(() => {
    // Si no hay documento o no es plantilla, limpia y no llama a la API
    if (!documentInfo.id || !documentInfo.is_kiubix_template) {
      dispatch(setComments({}));
      return;
    }

    getCommentsApi(documentInfo.id)
      .then(({ byPage }) => {
        dispatch(setComments(byPage || {}));
      })
      .catch(err => {
        console.error('Error cargando comentarios:', err);
        dispatch(setComments({}));
      });
  }, [documentInfo.id, documentInfo.is_kiubix_template, dispatch]);
  // ────────────────────────────────────────────────────────────────

  // Invitado con firma avanzada (FEA/FAB): en cuanto currentParticipant refleja su propia
  // firma ('signed', actualizado por updateStatusDocInvite tras firmar), hace scroll — sin
  // esperar a que el documento entero esté 'completed' si aún faltan invitados. positionsChecked
  // evita decidir la rama antes de saber con certeza si hubo templateId -3: si lo hubo
  // (firstSignedPage con valor) va a esa página; si no (caso default), va a la última hoja.
  // OJO: no se exige pagesRendered === numPages aquí. La lista está virtualizada (FixedSizeList)
  // y solo renderiza páginas visibles — en el caso default, Seguridata agrega la página nueva
  // al final, que queda fuera de vista tras recargar el PDF (el visor vuelve a la página 1), así
  // que pagesRendered nunca llegaba a alcanzar numPages y el scroll nunca se disparaba (solo
  // "se despertaba" con un scroll manual, que forzaba a react-window a montar más filas).
  // scrollToPage/scrollToPageAdvance ya se encargan de montar y llevar a la página destino.
  useEffect(() => {
    if (
      currentParticipant?.status === 'signed' && numPages > 0 &&
      positionsChecked &&
      (typeOfSign === typesSign.FAB || typeOfSign === typesSign.FEA)
    ) {
      setTimeout(() => {
        if (firstSignedPage) {
          scrollToPage(firstSignedPage);
        } else {
          scrollToPageAdvance(numPages);
        }
      }, 100);
    }
  }, [currentParticipant, numPages, typeOfSign, firstSignedPage, positionsChecked]);

  // Función que se llama cuando una página se renderiza correctamente
  const handlePageRenderSuccess = () => {
    // IMPORTANTE: Aseguramos que se actualice de forma asíncrona y segura
    // Usamos setTimeout para salir del ciclo de render actual de React y evitar "update during render"
    // y asegurar que el DOM esté listo.
    setTimeout(() => {
        setPagesRendered(prev => prev + 1);
    }, 0);
  };

  // Efecto para disparar el scroll cuando todas las páginas se hayan renderizado
  useEffect(() => {
    if (!autoScrollDone && pagesRendered === numPages && numPages > 0) {
      if (firstSignedPage) {
        scrollToPage(firstSignedPage);
        hasScrolledToSignature.current = true;
      }
      setAutoScrollDone(true); // Marcar que ya se realizó el scroll inicial
      onDocumentLoadComplete();
    }
  }, [pagesRendered, numPages, firstSignedPage, onDocumentLoadComplete, autoScrollDone]);

  useEffect(() => {
    if (scrollTarget) {
      scrollToPage(scrollTarget);
      dispatch(setScrollToPage(null));
    }
  }, [scrollTarget, dispatch]);

  // "Conecta" el sidebar de miniaturas (watch=false) con la página que se está
  // viendo en el visor principal: cada vez que `activePage` cambia (ya sea por
  // scroll manual del visor principal o por los botones del Control), este
  // sidebar se desplaza para mantener la miniatura activa a la vista.
  // 'smart' solo mueve el scroll si la miniatura no está ya visible, para no
  // pelear con un scroll manual del usuario dentro del propio sidebar.
  useEffect(() => {
    if (!watch && activePage != null && listRef.current) {
      listRef.current.scrollToItem(Number(activePage) - 1, 'smart');
    }
  }, [activePage, watch]);

  const scrollToPage = (pageNumber, attempts = 10) => {
    if (listRef.current) {
      listRef.current.scrollToItem(pageNumber - 1, 'start');
      console.log(`Scrolled to page ${pageNumber} (virtualized)`);
      return;
    }

    const attemptScroll = (remainingAttempts) => {
      if (refDocs.current.length === numPages) {
        if (pageNumber > 0 && pageNumber <= numPages) {
          const pageElement = refDocs.current[pageNumber - 1]?.current;
          if (pageElement) {
            // 👇 NUEVO: si tenemos contenedor de scroll, lo usamos explícitamente
            if (scrollContainerRef && scrollContainerRef.current) {
              const container = scrollContainerRef.current;
              const pageTop = pageElement.offsetTop;

              container.scrollTo({
                top: pageTop,
                behavior: 'smooth'
              });
            } else {
              // Fallback a comportamiento anterior
              pageElement.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest'
              });
            }

            console.log(`Scrolled to page ${pageNumber}`);
          } else {
            if (remainingAttempts > 0) {
              console.log(
                `Elemento de la página ${pageNumber} no encontrado. Reintentando... (${remainingAttempts} intentos restantes)`
              );
              setTimeout(() => attemptScroll(remainingAttempts - 1), 100);
            } else {
              console.log(`Elemento de la página ${pageNumber} no se encontró tras múltiples intentos.`);
            }
          }
        } else {
          console.log(`Número de página inválido: ${pageNumber}`);
        }
      } else {
        console.log('Las páginas del PDF no están completamente inicializadas.');
      }
    };

    attemptScroll(attempts);
  };

  useEffect(() => {
    refDocs.current = Array.from(new Array(numPages), () => React.createRef());
    // Reinicia el contador de páginas renderizadas
    setPagesRendered(0);
  }, [numPages]);

  const scrollPixels = useSelector(s => s.document.scroll);

  useEffect(() => {
    if (listRef.current && scrollPixels !== undefined) {
      listRef.current.scrollTo(scrollPixels);
    }
  }, [scrollPixels]);

  useEffect(() => {
    // positionsChecked evita decidir la rama antes de saber con certeza si hubo
    // templateId -3 (la respuesta de get-signature-positions podría no haber llegado
    // aún justo cuando el status pasa a 'completed').
    if (numPages > 0 && documentInfo.status === 'completed' && positionsChecked && (documentInfo.typeOfSignOwner === 'FEA' || documentInfo.typeOfSignOwner === 'FAB')) {
      // Utiliza un pequeño retraso para asegurar que el DOM ha sido actualizado completamente
      setTimeout(() => {
        if (firstSignedPage) {
          // templateId -3: las firmas quedan en las coordenadas guardadas, no en una hoja
          // nueva al final — vamos a la primera página que realmente tiene una firma.
          scrollToPage(firstSignedPage);
        } else {
          // Caso default de Seguridata: la(s) firma(s) se agregan en una hoja nueva al final.
          scrollToPageAdvance(numPages);
        }
      }, 100);
    }
  }, [numPages, documentInfo.status, documentInfo.typeOfSignOwner, firstSignedPage, positionsChecked]); // Dependencias del efecto

  // Firma simple (Signly/autógrafa) con el documento ya completado: lleva el visor a la página donde quedaron
  // las firmas. Para FEA/FAB ya lo hace el efecto de arriba. Se hace aquí porque la posición de las firmas
  // llega por un fetch aparte (positionsChecked/firstSignedPage) y suele resolverse DESPUÉS de que las
  // páginas se renderizaron, cuando los scrolls iniciales ya corrieron sin esa información. Si el documento se
  // firmó ignorando las líneas de firma (ignoreSignatureLines) no hay posiciones y no se mueve nada.
  const simpleScrollDoneRef = useRef(null);
  useEffect(() => {
    const isAdvanced = [documentInfo?.typeOfSignOwner, typeOfSign].some(
      t => t === typesSign.FAB || t === typesSign.FEA
    );
    if (
      !watch || readOnly || isAdvanced || !positionsChecked || !firstSignedPage ||
      numPages <= 0 || documentInfo?.status !== statusDocument.COMPLETED ||
      documentInfo?.ignoreSignatureLines || ignoreSignatureLines
    ) return undefined;

    const key = `${documentInfo?.id}-${firstSignedPage}`;
    if (simpleScrollDoneRef.current === key) return undefined;
    const timer = setTimeout(() => {
      simpleScrollDoneRef.current = key;
      scrollToPage(firstSignedPage);
    }, 400);
    return () => clearTimeout(timer);
  }, [watch, readOnly, typeOfSign, positionsChecked, firstSignedPage, numPages, documentInfo?.id, documentInfo?.status, documentInfo?.typeOfSignOwner, documentInfo?.ignoreSignatureLines, ignoreSignatureLines]);

  const scrollToPageAdvance = (pageNumber, attempts = 10, delay = 300) => {
    if (listRef.current) {
      setTimeout(() => {
        listRef.current.scrollToItem(pageNumber - 1, 'end');
        console.log(`Scrolled to last page ${pageNumber} (advance virtualized)`);
      }, delay);
      return;
    }

    const attemptScroll = (remainingAttempts) => {
      const pageElement = refDocs.current[pageNumber - 1]?.current;
      if (pageElement) {
        if (scrollContainerRef && scrollContainerRef.current) {
          const container = scrollContainerRef.current;

          // Queremos que se vea la parte baja de la última página,
          // pero sin “comerse” el contenido superior fuera del contenedor.
          const pageTop = pageElement.offsetTop;
          const pageHeight = pageElement.offsetHeight;
          const pageBottom = pageTop + pageHeight;

          const targetTop = Math.max(
            0,
            pageBottom - container.clientHeight
          );

          setTimeout(() => {
            container.scrollTo({
              top: targetTop,
              behavior: 'smooth'
            });
            console.log(`Scrolled to last page ${pageNumber} (advance)`);
          }, delay);
        } else {
          // Fallback al comportamiento original si no hay contenedor
          setTimeout(() => {
            pageElement.scrollIntoView({
              behavior: 'smooth',
              block: 'end'
            });
            console.log(`Scrolled to last page ${pageNumber} (fallback)`);
          }, delay);
        }
      } else {
        if (remainingAttempts > 0) {
          console.log(
            `Elemento de la página ${pageNumber} no encontrado. Reintentando... (${remainingAttempts} intentos restantes)`
          );
          setTimeout(() => attemptScroll(remainingAttempts - 1), delay);
        } else {
          console.log(`Elemento de la página ${pageNumber} no se encontró tras múltiples intentos.`);
        }
      }
    };

    attemptScroll(attempts);
  };

  // Tamaño en pantalla del cuadro guía del selector (misma escala que las cajas guardadas).
  const cursorGeometry = getSignatureGeometry(getParticipantTypeOfSign(currentParticipant, participants, documentInfo, email));
  const cursorScaleX = dimenssions?.width && documentInfo?.width ? dimenssions.width / documentInfo.width : 1;
  const cursorScaleY = dimenssions?.height && documentInfo?.height ? dimenssions.height / documentInfo.height : 1;

  const handleMouseMove = (event) => {
    if (!isSignaturePositionModeActive) return;
    const container = containerRef.current;
    if (!container) return;
    const { left, top } = container.getBoundingClientRect();
    setCursorPos({
      x: event.clientX - left,
      y: event.clientY - top,
      visible: true
    });
  };

  useEffect(() => {
    if (!disableSignatureSelector && isSignaturePositionModeActive) {
      if (isMobile) {
        // En mobile: posicionar la caja pending en la página que el usuario está viendo
        let currentPage = parseInt(page, 10) || 1;
        const centerX = documentInfo?.width ? Math.max(0, documentInfo.width / 2 - 75) : 50;
        const pageHeight = documentInfo?.height || 800; // alto de una página individual
        let centerY = Math.max(0, pageHeight / 2 - 30); // centro vertical de la página
        // La "página actual" de redux se calcula por scroll/IntersectionObserver y en un teléfono puede ir
        // un paso atrás (p. ej. sigue en la 1 aunque se esté viendo la 2): la caja nacía en una hoja que no
        // se ve y parecía que no aparecía. Se toma la hoja realmente visible en pantalla y, si hay una, la
        // caja se centra en su parte visible.
        let pageAlreadyVisible = false;
        try {
          const viewport = containerRef.current.getBoundingClientRect();
          let best = null;
          containerRef.current.querySelectorAll('[data-page-container="true"]').forEach((el) => {
            const rect = el.getBoundingClientRect();
            const visibleTop = Math.max(rect.top, viewport.top);
            const visibleBottom = Math.min(rect.bottom, viewport.bottom);
            const overlap = visibleBottom - visibleTop;
            if (overlap > 0 && (!best || overlap > best.overlap)) best = { el, rect, overlap, visibleTop, visibleBottom };
          });
          if (best && best.overlap >= 80 && best.rect.height > 0) {
            const visiblePage = parseInt(best.el.getAttribute('data-page-index'), 10);
            if (visiblePage) {
              currentPage = visiblePage;
              const pdfPerPx = pageHeight / best.rect.height;
              const visibleCenterPdf = ((best.visibleTop + best.visibleBottom) / 2 - best.rect.top) * pdfPerPx;
              centerY = Math.min(Math.max(0, visibleCenterPdf - 30), Math.max(0, pageHeight - 60));
              pageAlreadyVisible = true;
            }
          }
        } catch (e) {
          // si no se puede medir, queda el comportamiento anterior (página de redux, centrada)
        }
        setPendingPosition({ x: centerX, y: centerY, page: currentPage });
        // Scroll a la página solo si no se está viendo ya, para que la caja sea visible
        if (!pageAlreadyVisible && listRef.current) {
          listRef.current.scrollToItem(currentPage - 1, 'start');
        }
      } else {
        // En desktop centramos el recuadro de preview
        const { width, height } = containerRef.current.getBoundingClientRect();
        setCursorPos({ x: width / 2, y: height / 2, visible: true });
      }
      // Overlay de instrucciones en ambas plataformas (máximo 2 veces)
      if (introShowCountRef.current < 2) {
        setShowSelectionIntro(true);
        introShowCountRef.current += 1;
      }
    }
  }, [isSignaturePositionModeActive, disableSignatureSelector]);

  // justo después de tu useState de showSelectionIntro
  useEffect(() => {
    if (!showSelectionIntro) return;
    const endIntro = e => {
      e.stopPropagation();
      // En el teléfono, tras un touchstart el navegador genera un click sobre lo que haya DEBAJO cuando el
      // aviso ya desapareció. Se cancela para que ese toque solo cierre el aviso y no pulse nada más.
      if (e.type === 'touchstart' && e.cancelable) e.preventDefault();
      setShowSelectionIntro(false);
      justClosedIntro.current = true;
      overlayDismissAtRef.current = Date.now();
    };
    document.addEventListener('mousedown', endIntro, { capture: true, passive: false });
    document.addEventListener('touchstart', endIntro, { capture: true, passive: false });
    return () => {
      document.removeEventListener('mousedown', endIntro, { capture: true, passive: false });
      document.removeEventListener('touchstart', endIntro, { capture: true, passive: false });
    };
  }, [showSelectionIntro]);

  const handleTouchMove = event => {
    if (!isSignaturePositionModeActive) return;
    const t = event.touches[0];

    const dx = Math.abs(t.clientX - touchStateRef.current.startX);
    const dy = Math.abs(t.clientY - touchStateRef.current.startY);
    if (dx > 10 || dy > 10) touchStateRef.current.moved = true;

    const { left, top } = containerRef.current.getBoundingClientRect();
    setCursorPos({
      x: t.clientX - left,
      y: t.clientY - top,
      visible: true
    });
  };

  const handleTouchEnd = event => {
    if (!isSignaturePositionModeActive) return;

    // Si acabamos de cerrar el overlay, ignorar este touchend para no mover/crear la pending box
    if (justClosedIntro.current) {
      justClosedIntro.current = false;
      touchStateRef.current.active = false;
      touchStateRef.current.lastEndAt = Date.now();
      return;
    }

    // si hubo desplazamiento, no es tap
    if (touchStateRef.current.moved) {
      touchStateRef.current.active = false;
      touchStateRef.current.lastEndAt = Date.now();
      return;
    }

    const t = event.changedTouches && event.changedTouches[0];
    if (t) {
      const calculated = calculatePositionFromEvent({
        clientX: t.clientX,
        clientY: t.clientY,
        target: event.target
      });
      if (calculated) setPendingPosition(calculated);
    }

    touchStateRef.current.active = false;
    touchStateRef.current.lastEndAt = Date.now();

    // evita que el click fantasma dispare después del touchend
    // if (event.cancelable) event.preventDefault();
    event.stopPropagation();
  };

  // Cambiar el cursor mediante efectos secundarios
  useEffect(() => {
    const pdfContainer = document.getElementById('pdf-container');
    if (pdfContainer) {
      pdfContainer.style.cursor = isSignaturePositionModeActive ? 'crosshair' : 'default';
      // En mobile el touchAction lo maneja el FixedSizeList directamente (pan-y pinch-zoom / none)
      if (!isMobile) {
        pdfContainer.style.touchAction = isSignaturePositionModeActive ? 'none' : 'auto';
      }
      pdfContainer.style.overscrollBehavior = isSignaturePositionModeActive ? 'contain' : 'auto';
      // Mientras se coloca una posición de firma, evitar que la capa de texto (seleccionable)
      // interfiera con el clic/tap que registra la posición.
      pdfContainer.classList.toggle('pdf-signature-mode-active', isSignaturePositionModeActive);
    } else {
      console.error('El contenedor de PDF no se encontró en el DOM');
    }
  }, [isSignaturePositionModeActive, numPages]); // Añadir numPages podría ayudar si el contenido se carga dinámicamente

  // Limpiar caja pending si se desactiva el modo de posicionamiento o cambia el participante activo
  useEffect(() => {
    if (!isSignaturePositionModeActive) setPendingPosition(null);
  }, [isSignaturePositionModeActive]);

  // Limpiar caja pending si el participante al que pertenece fue eliminado de la lista
  useEffect(() => {
    if (!pendingPosition || !currentParticipant) return;
    const stillExists = participants?.some(p => p.email === currentParticipant.email);
    if (!stillExists) setPendingPosition(null);
  }, [participants]);

  useEffect(() => {
    setPendingPosition(null);
  }, [currentParticipant]);

  const updateSizeDoc = (retries = 5) => {
    // console.log('updateSizeDoc - watch:', watch);
    if (!watch && !isFullScreen) return;

    // Verificar si realmente tenemos páginas renderizadas antes de intentar medir
    if (pagesRendered < 1) {
        // console.log('updateSizeDoc: Esperando renderizado de páginas...');
        return; 
    }

    // Buscar el primer elemento de página disponible (no necesariamente el índice 0)
    // Esto es crucial para virtualización donde la pág 0 puede no estar renderizada
    const firstAvailableRef = refDocs?.current?.find(ref => ref.current);

    if (firstAvailableRef && firstAvailableRef.current) {
      const container = firstAvailableRef.current;
      // Intentamos seleccionar el canvas dentro del contenedor
      const canvas = container.querySelector('canvas');
      if (canvas) {
        const width = canvas.offsetWidth;
        const height = canvas.offsetHeight;
        // console.log('updateSizeDoc - dimensiones obtenidas (canvas):', { width, height });
        dispatch(setDimessions({ width, height }));
        observeRefs();
      } else if (retries > 0) {
        // console.warn('updateSizeDoc: canvas no encontrado. Reintentando...', retries);
        setTimeout(() => updateSizeDoc(retries - 1), 500);
      } else {
        console.error('updateSizeDoc: No se pudo obtener el canvas después de varios reintentos.', container);
      }
    } else if (retries > 0) {
      // console.warn('updateSizeDoc: refDocs o el primer elemento no están definidos. Reintentando...', retries);
      setTimeout(() => updateSizeDoc(retries - 1), 500);
    } else {
      console.error('updateSizeDoc: No se pudo obtener ningún elemento de página visible después de varios reintentos.');
    }
  };

  useEffect(() => {
    // console.log('Positions:', positions);
    if (numPages > 0 && positions && Object.keys(positions).length > 0 && !hasScrolledToSignature.current) {
      const firstPageWithSignature = Object.keys(positions).find(page =>
        positions[page].some(pos =>
          pos.email === email && pos.type === 'signature'
        )
      );

      console.log('First page with signature:', firstPageWithSignature);

      if (firstPageWithSignature) {
        setTimeout(() => {
          scrollToPage(parseInt(firstPageWithSignature));
          hasScrolledToSignature.current = true;
        }, 300);
      }
    }
  }, [positions, email, numPages]);

  useEffect(() => {
    if (
      triggerScrollAfterLoad &&
      pagesRendered === numPages &&
      numPages > 0 &&
      positions &&
      Object.keys(positions).length > 0 &&
      !hasScrolledAfterSign.current
    ) {
      const firstPageWithSignature = Object.keys(positions).find(page =>
        positions[page].some(pos => pos.email === email && pos.type === 'signature')
      );
      console.log('Scroll triggered after load, first page with signature:', firstPageWithSignature);
      if (firstPageWithSignature) {
        setTimeout(() => {
          scrollToPage(parseInt(firstPageWithSignature));
          hasScrolledAfterSign.current = true;
        }, 500);
      }
    }
  }, [triggerScrollAfterLoad, pagesRendered, numPages, positions, email]);

  // useEffect(() => {
  //   if (
  //     numPages > 0 &&
  //     Object.keys(positions).length > 0 &&
  //     (documentInfo.status === 'completed' || currentParticipant.status === 'signed')
  //   ) {
  //     // Buscar la primera página con una firma ya completada
  //     const firstSignedPage = Object.keys(positions).find(page =>
  //       positions[page].some(pos => pos.email === email && pos.type === 'signature')
  //     );

  //     if (firstSignedPage) {
  //       setTimeout(() => {
  //         scrollToPage(parseInt(firstSignedPage)); // Realiza el scroll a la página encontrada
  //       }, 100);
  //     }
  //   }
  // }, [positions, email, numPages, documentInfo.status, currentParticipant.status]);

  // const sendDiscordMessage = async (error, documentId, userId) => {
  //   try {
  //     const environment = import.meta.env.VITE_APP_ENV; // o, si lo prefieres, puedes usar process.env.NODE_ENV
  //     // Formatea el mensaje incluyendo el ambiente al inicio
  //     const message = `🚨 [${environment.toUpperCase()}] ❌ Error al generar PDF en DocumentPdf.jsx
  // 🧠 Error: ${error?.message || error}
  // 📄 documentId: ${documentId}
  // 👤 userId: ${userId}`;

  //     await axios.post(
  //       'https://discord.com/api/webhooks/REDACTED',
  //       { content: message }
  //     );

  //     console.log('Mensaje enviado con éxito a Discord');
  //   } catch (e) {
  //     console.error('Error al enviar el mensaje a Discord:', e);
  //   }
  // };

  async function generatePdf () {
    if (!enablePdfManipulation) return;
    try {
      // cloudPDF.data ya es un ArrayBuffer
      const pdfBytes = cloudPDF.data instanceof ArrayBuffer
        ? new Uint8Array(cloudPDF.data)
        : cloudPDF.data; // si ya fuera Uint8Array
      const pdfDoc = await PDFDocument.load(pdfBytes);
      if (onPdfDocCreated) onPdfDocCreated(pdfDoc);
    } catch (error) {
      console.error('Error al cargar el PDF:', error);
    }
  }

  // Efecto para observar cambios en el número de páginas
  // y actualizar el tamaño del documento cuando se renderiza al menos una página
  useEffect(() => {
    // Con virtualización, no esperamos a que todas las páginas (numPages) se rendericen,
    // sino que basta con que haya páginas renderizadas (pagesRendered > 0) para medir
    // dimensiones y marcar "load complete".
    if (pagesRendered > 0 && numPages > 0) {
      updateSizeDoc();
      // Si es la primera vez que se carga algo
      if (!autoScrollDone) {
          setAutoScrollDone(true);
          onDocumentLoadComplete();
          if (firstSignedPage) {
              scrollToPage(firstSignedPage);
              hasScrolledToSignature.current = true;
          }
      }
    }
  }, [pagesRendered, numPages]);

  const onDocumentLoadSuccess = async ({ numPages: nextNumPages }) => {
    // console.log("Documento cargado con éxito, número de páginas:", nextNumPages);
    dispatch(setNumPages(nextNumPages));
    // Reinicializamos refDocs con el tamaño correcto
    refDocs.current = Array.from(new Array(nextNumPages), () => React.createRef());
    
    // OPTIMIZACIÓN DE MEMORIA:
    // Desactivamos la carga automática de pdf-lib (generatePdf) aquí.
    // pdf-lib carga una segunda copia del PDF en memoria que no es necesaria para la visualización.
    // Solo deberíamos cargarla cuando el usuario vaya a realizar una acción de edición (firmar/guardar).
    // generatePdf(); 
    
    if (watch && isMobileFull) {
      observeRefs();
    }
  };

  // Función para reportar errores al backend
  const logErrorToBackend = async (errorMsg) => {
    try {
      // Obtener token disponible: usuario con sesión (cookie) o invitado (sessionStorage)
      const cookieToken = document.cookie.match(/(?:^|;\s*)token=([^;]+)/)?.[1];
      const guestToken = sessionStorage.getItem('tokenInvite');
      const token = cookieToken || guestToken;

      await axios.post(`${process.env.REACT_APP_BASE_URL}log-client-event`, {
        message: `Error renderizando PDF: ${errorMsg}`,
        origin: 'Frontend - DocumentPdf',
        context: `DocID: ${documentInfo?.id ?? 'unknown'}`,
        type: 'error'
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
    } catch (e) {
      console.error('No se pudo enviar log al backend:', e);
    }
  };

  const onDocumentLoadError = (error) => {
    console.error('Error al cargar el documento PDF:', error);
    logErrorToBackend(error.message || 'Unknown PDF Load Error');
    // Aquí podrías mostrar un toast o UI de error si quisieras
  };

  useEffect(() => {
    updateSizeDoc();
  }, [zoom]);

  function observeRefs () {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.reverse().forEach((entry) => {
          if (entry.isIntersecting) {
            const pageNumber = entry.target
              .querySelector('[data-page-number]')
              .getAttribute('data-page-number');
            dispatch(setPage(pageNumber));
          }
        });
      },
      { threshold: 0.5 }
    );
    const validRefs = refDocs.current.filter((ref) => ref.current);

    validRefs.forEach((ref) => {
      observer.observe(ref.current);
    });

    return () => {
      validRefs.forEach((ref) => {
        observer.unobserve(ref.current);
      });
    };
  }

  const handleMaker = (index) => {
    // console.log(`handleMaker: page=${index + 1}, status=${documentInfo.status}, hasParticipants=${moreParticipants(participants, email)}, ignoreSignatureLines=${ignoreSignatureLines}`);
    // console.log(`Manejador de firmas para la página ${index + 1}:`, documentInfo?.status, moreParticipants(participants, email));

    if (!watch || readOnly) return;

    if (documentInfo?.status === statusDocument.SEND && ignoreSignatureLines) {
      return <Sign page={index + 1} />;
    }

    if (documentInfo?.status === statusDocument.SEND && !ignoreSignatureLines) {
      return <Sign page={index + 1} />;
    }

    // Para el escenario "Firmaré solo yo" -> "Solo me interesa firmar" (ignoreSignatureLines)
    // El dueño necesita ver su cuadro de firma (Sign) directamente en INIT.
    if (documentInfo?.status === statusDocument.INIT && ignoreSignatureLines) {
      return <Sign page={index + 1} />;
    }

    // Si estamos en la opción "Sí tiene líneas de firma", NO renderizar el componente SignPos clásico (las líneas de dibujo)
    // durante INIT ni PENDING, porque para eso estamos usando las nuevas cajas visuales azules (`signaturePositions`).
    if (hasExistingSignatureLines && (documentInfo?.status === statusDocument.INIT || documentInfo?.status === statusDocument.PENDING)) {
      // PERO, si estamos en PENDING, SÍ queremos que el dueño vea el cuadro de firma <Sign> para poder firmar.
      if (documentInfo?.status === statusDocument.PENDING && (participants.length === 1 && participants[0].email === email || moreParticipants(participants, email))) {
        return <Sign page={index + 1} />;
      }
      return null;
    }

    // Para firmas avanzadas (FEA/FAB), el nuevo cuadro azul punteado (`signaturePositions`) es la ÚNICA interfaz
    // visual deseada para seleccionar coordenadas en INIT. No queremos que aparezca el `SignPos` viejo encima de él.
    // Verificamos si el dueño o los participantes usan FEA/FAB.
    const isOwnerAdvanced = documentInfo?.typeOfSignOwner === 'FEA' || documentInfo?.typeOfSignOwner === 'FAB';
    const isAnyParticipantAdvanced = participants.some(p => p.typeofSign === 'FEA' || p.typeofSign === 'FAB');
    if ((isOwnerAdvanced || isAnyParticipantAdvanced) && documentInfo?.status === statusDocument.INIT) {
      return null;
    }

    // Cuando solo el dueño está firmando y necesita agregar una posición de firma.
    if (documentInfo?.status === statusDocument.INIT && participants.length === 1 && participants[0].email === email) {
      return <SignPos page={index + 1} />;
    }

    if (documentInfo?.status === statusDocument.PENDING && participants.length === 1 && participants[0].email === email) {
      return <Sign page={index + 1} />;
    }

    // FIX: Renderizar Sign cuando estamos en PENDING y hay más participantes (whithInvites)
    // Esto permite que el dueño vea su caja de firma (Sign) cuando el estado es PENDING.
    if (documentInfo?.status === statusDocument.PENDING && moreParticipants(participants, email)) {
      return <Sign page={index + 1} />;
    }

    if (
      documentInfo?.status === statusDocument.INIT &&
      moreParticipants(participants, email)
    ) {
      return <SignPos page={index + 1} />;
    } else if (
      documentInfo?.status === statusDocument.INIT &&
      !moreParticipants(participants, email)
    ) {
      return <Sign page={index + 1} />;
    } else if (documentInfo?.status === statusDocument.PROCESSED) {
      return <Sign page={index + 1} />;
    } else if (isShared) {
      return <Sign page={index + 1} />;
    }
  };

  const handleRightClick = event => {
    if (event.cancelable) event.preventDefault(); // evita que aparezca el menú nativo
    
    // 1. Si estamos en modo selección de firma, la prioridad es cancelar ese modo
    if (isSignaturePositionModeActive) {
      dispatch(setSignaturePositionMode(false));
      setCursorPos(pos => ({ ...pos, visible: false }));
      return;
    }

    // 2. Si NO estamos en modo selección y es un template Kiubix, abrimos el comentario
    if (documentInfo.is_kiubix_template) {
      const pageEl = event.target.closest('[data-page-number]');
      if (!pageEl) return;
      const page = Number(pageEl.getAttribute('data-page-number'));
      const rect = pageEl.getBoundingClientRect();
      const clickX = event.clientX - rect.left;
      const clickY = event.clientY - rect.top;
      setInputState({
        visible: true,
        page,
        x: clickX,
        y: clickY,
        xRatio: clickX / rect.width,
        yRatio: clickY / rect.height
      });
    }
  };

  const handleTouchStart = event => {
    if (!isSignaturePositionModeActive) return;

    if (event.touches.length === 2) {
      // gesto de zoom: cancela selección
      dispatch(setSignaturePositionMode(false));
      setCursorPos(pos => ({ ...pos, visible: false }));
      setShowSelectionIntro(false);
      return;
    }

    const t = event.touches[0];
    touchStateRef.current.startX = t.clientX;
    touchStateRef.current.startY = t.clientY;
    touchStateRef.current.moved = false;
    touchStateRef.current.active = true;
  };

  // Calcula la posición de la firma en unidades PDF a partir del evento de clic/tap.
  // Retorna { x, y, page } o null si hay error o bloqueo por FEA/FAB.
  const calculatePositionFromEvent = ({ clientX, clientY, target }) => {
    if (!isSignaturePositionModeActive) return null;
    // Sin firmante seleccionado no hay a quién asignar la posición (antes lanzaba TypeError).
    if (!currentParticipant) return null;

    const pageElement = target.closest('[data-page-container="true"]');
    if (!pageElement) {
      alert('La posición de firma está fuera de los límites del documento.');
      return null;
    }

    // Utilizar la escala y dimensiones exactas del contenedor relativo para que el clic
    // coincida matemáticamente con la posición absoluta del renderizado.
    const rect = pageElement.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) {
      alert('No se pudieron obtener las dimensiones del documento renderizado.');
      return null;
    }

    const docWidth = documentInfo.width;
    const docHeight = documentInfo.height;
    if (!docWidth || !docHeight) {
      alert('Las dimensiones originales del documento no están disponibles.');
      return null;
    }

    // Usamos las dimensiones base sin importar si la vista tiene márgenes de pantalla o contenedores ajenos
    // porque `pagePositions` y SeguriData esperan la matemática cruda.
    const renderScaleX = rect.width / docWidth;
    const renderScaleY = rect.height / docHeight;

    const clickX = clientX - rect.left;
    const clickY = clientY - rect.top;

    const xOriginal = clickX / renderScaleX;
    const yOriginal = clickY / renderScaleY;

    // Leer la página desde el atributo DOM — siempre es un entero correcto,
    // independiente del estado Redux `page` que puede ser string o la primera página visible.
    const currentPage = parseInt(pageElement.getAttribute('data-page-index'), 10) || parseInt(page, 10);
    const isOwner = currentParticipant.email === email;

    const fullParticipant = participants.find(p => p.email === currentParticipant?.email) || currentParticipant;
    const currentTypeOfSign = isOwner ? documentInfo.typeOfSignOwner : fullParticipant?.typeofSign;
    const isAdvancedSignature = currentTypeOfSign === 'FEA' || currentTypeOfSign === 'FAB';

    // Lo que se guarda es la posición que produce exactamente la huella de la firma que el usuario ve
    // (constants/signatureGeometry.js). Desktop: la esquina superior izquierda del cuadro guía (el cursor)
    // es la esquina de la firma. Mobile: la firma queda centrada sobre el punto tocado y después se ajusta
    // arrastrando la caja pendiente.
    const geometry = getSignatureGeometry(currentTypeOfSign);
    const footprintLeft = isMobile ? xOriginal - geometry.width / 2 : xOriginal;
    const footprintTop = isMobile ? yOriginal - geometry.height : yOriginal;
    const { x: xToSave, y: yToSave } = getPositionFromFootprintOrigin(footprintLeft, footprintTop, geometry, docWidth, docHeight);

    // Validación: Prevenir múltiples firmas avanzadas (FEA/FAB) para el mismo participante
    if (isAdvancedSignature) {
      const hasPositionAlready = signaturePositions && signaturePositions.some(pos => pos.email === fullParticipant?.email);
      if (hasPositionAlready) {
        toast.error('Las firmas avanzadas (FEA o FAB) solo permiten colocar una posición de firma por participante en todo el documento.', {
          duration: 5000
        });
        dispatch(setSignaturePositionMode(false));
        setCursorPos(pos => ({ ...pos, visible: false }));
        return null; // Abortar la creación de la coordenada
      }
    }

    return { x: xToSave, y: yToSave, page: currentPage };
  };

  // Guarda en Redux y en la base de datos una posición de firma ya calculada y confirmada por el usuario.
  const saveCalculatedPosition = ({ x, y, page: currentPage }) => {
    const isOwner = currentParticipant.email === email;
    const positionType = 'lineOfSignature'; // Se mantiene para compatibilidad con el backend
    const tempId = `temp-${Date.now()}`;

    dispatch(addSignaturePosition({
      x,
      y,
      page: currentPage,
      email: currentParticipant.email,
      id: tempId
    }));

    dispatch(updatePositionParticipant({
      participant: currentParticipant,
      page: currentPage,
      x,
      y,
      type: positionType,
      signed: false,
      id: tempId
    }));

    setCursorPos(pos => ({ ...pos, visible: false }));
    dispatch(setSignaturePositionMode(false));

    const saver = isOwner
      ? savePositionsinDB(userInfo, documentInfo.id, currentPage, x, y, positionType)
      : savePositionsinDB(currentParticipant, documentInfo.id, currentPage, x, y, positionType);

    saver
      .then((response) => {
        dispatch(updateSignaturePositionId({ tempId, realId: response?.id }));
        dispatch(updatePositionId({ tempId, realId: response?.id })); // Sincronizar ID
      })
      .catch((err) => {
        console.error('Error guardando posición:', err);
        dispatch(removeSignaturePositionById(tempId));
        dispatch(removePositionById(tempId)); // Sincronizar
        toast.error('No se pudo guardar la posición, inténtalo de nuevo.');
      });
  };

  // Función para manejar el clic en la página del PDF para el modo de seleccion posición de firma
  // Se encarga de calcular la posición de la firma y guardarla en la base de datos
  // y en el estado global de Redux
  // Se asegura de que la posición esté dentro de los límites del documento
  // y ajusta la posición si es necesario
  // También se encarga de mostrar un mensaje de error si la posición está fuera de los límites
  // y de ocultar el cursor de firma
  // y desactivar el modo de seleccion de posición de firma.
  const handlePageClick = (event) => {
    if (!isSignaturePositionModeActive) return;

    // si justo cerramos el overlay, ignorar este primer click
    if (justClosedIntro.current) {
      justClosedIntro.current = false;
      return;
    }

    const calculated = calculatePositionFromEvent({
      clientX: event.clientX,
      clientY: event.clientY,
      target: event.target
    });
    if (calculated) saveCalculatedPosition(calculated);
  };

  // Función para manejar el doble clic en la página del PDF para agregar un comentario en las plantillas de KIUBIX
  // Se encarga de calcular la posición del comentario y mostrar el input para agregarlo.
  const handleDoubleClick = event => {
    // ANTES: abría comentarios en plantillas Kiubix
    // AHORA: se movió a handleRightClick para evitar conflictos con la selección de firmas
    // if (!documentInfo.is_kiubix_template) return;
    // ...
  };

  // Función para guardar la posición de la firma en la base de datos
  // Se encarga de enviar la posición de la firma al servidor
  // y de manejar la respuesta.
  const savePositionsinDB = async (participant, documentId, page, x, y, type = 'lineOfSignature') => {
    try {
      // Realizamos la petición sin await para que se ejecute en background
      const response = axios.post(`${process.env.REACT_APP_BASE_URL}signs/savePositions`, {
        signerId: participant.email === userInfo.email ? userInfo.id : participant.id,
        documentId,
        page,
        x,
        y,
        isOwner: participant.email === userInfo.email,
        type: type // Le pasamos el type para que el backend sepa si es solo firma o línea
      },
      {
        withCredentials: true // Envia automáticamente la cookie httpOnly
      }
      );
      // Retornamos directamente el `res.data` para reducir latencia
      return response.then(res => res.data);
    } catch (error) {
      console.error('Error en savePositionsinDB:', error);
    }
  };

  // useEffect(() => {
  //   const handleMouseMove = (event) => {
  //     if (isSignaturePositionModeActive) {
  //       setCursorPos({ x: event.clientX, y: event.clientY, visible: true });
  //     }
  //   };

  //   window.addEventListener('mousemove', handleMouseMove);
  //   return () => {
  //     window.removeEventListener('mousemove', handleMouseMove);
  //   };
  // }, [isSignaturePositionModeActive, cursorPos.visible]);

  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

  // Altura real que queda para el visor en mobile. Con la pantalla bloqueada (documento inmóvil, ver
  // hooks/useLockDocumentScroll) la ventana ya no se desplaza, y una lista de 80 % de la pantalla puesta debajo
  // del encabezado, el aviso y los controles se sale de la zona visible: la parte final de la última hoja
  // quedaba fuera de la pantalla y no había forma de verla. Se usa lo que realmente queda desde la parte alta
  // del visor hasta el borde inferior visible. Sin pantalla bloqueada se conserva el cálculo anterior.
  const [mobileListHeight, setMobileListHeight] = useState(null);
  useEffect(() => {
    if (!isMobile || !watch) { setMobileListHeight(null); return undefined; }
    const measure = () => {
      const el = containerRef.current;
      if (!el || !document.documentElement.classList.contains('app-doc-locked')) { setMobileListHeight(null); return; }
      const vv = window.visualViewport;
      const bottom = vv ? vv.height : window.innerHeight;
      const available = Math.floor(Math.min(bottom - el.getBoundingClientRect().top, window.innerHeight));
      setMobileListHeight(available >= 160 ? available : null);
    };
    measure();
    const timers = [150, 600, 1500].map((ms) => setTimeout(measure, ms)); // el encabezado y el aviso terminan de acomodarse después
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);
    if (window.visualViewport) window.visualViewport.addEventListener('resize', measure);
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro && containerRef.current) {
      ro.observe(containerRef.current);
      if (containerRef.current.parentElement) ro.observe(containerRef.current.parentElement);
    }
    return () => {
      timers.forEach(clearTimeout);
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
      if (window.visualViewport) window.visualViewport.removeEventListener('resize', measure);
      if (ro) ro.disconnect();
    };
  }, [isMobile, watch, isFullScreen, numPages]);

  useEffect(() => {
    if (!containerRef.current) return;
    
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerSize({
          width: entry.contentRect.width,
          height: entry.contentRect.height
        });
      }
    });

    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  return (
  <div
    id="pdf-container"
    ref={containerRef}
    onMouseMove={handleMouseMove}
    onTouchMove={handleTouchMove}
    onTouchStart={handleTouchStart}
    onTouchEnd={handleTouchEnd}
    onClick={event => {
      if (showSelectionIntro) return;

      const now = Date.now();
      // ignorar click si acaba de cerrarse el overlay
      if (now - overlayDismissAtRef.current < 350) return;
      // ignorar ghost click que sigue al touchend
      if (now - touchStateRef.current.lastEndAt < 350) return;

      handlePageClick(event);
    }}
    onDoubleClick={handleDoubleClick}
    onContextMenu={handleRightClick}
    style={{ position: 'relative', height: '100%', width: '100%' }}
  >
    {isMobile && watch && !readOnly && documentInfo?.status !== statusDocument.SEND && documentInfo?.status !== statusDocument.COMPLETED && documentInfo?.status !== statusDocument.PENDING && documentInfo?.status !== statusDocument.PROCESSED && documentInfo?.status !== statusDocument.CANCELLED && signaturePositions?.length > 0 && (
      <SavedSignaturesOverlay
        positions={signaturePositions}
        participants={participants}
        documentInfo={documentInfo}
        userInfo={userInfo}
        onDelete={(pos, participant, isOwner, globalIndex) => {
          dispatch(removeSignaturePositionById ? removeSignaturePositionById(pos.id) : removeSignaturePosition(globalIndex));
          dispatch(removePositionById(pos.id)); // Sincronizar borrado con positions para firmas simples
          if (pos.id) {
            axios.post(`${process.env.REACT_APP_BASE_URL}signs/deletePosition`, {
              id: pos.id,
              signerId: participant?.id,
              documentId: documentInfo?.id,
              isOwner
            }, { withCredentials: true }).catch(console.error);
          }
        }}
      />
    )}
    {isMobile && watch && pendingPosition && (
      <PendingSignatureOverlay
        pendingPosition={pendingPosition}
        setPendingPosition={setPendingPosition}
        geometry={cursorGeometry}
        documentInfo={documentInfo}
        label={(() => {
          const full = participants?.find(p => p.email === currentParticipant?.email);
          return full ? `${full.name} ${full.last_name || ''}`.trim() : (currentParticipant?.email || '');
        })()}
        onCancel={() => { dispatch(setSignaturePositionMode(false)); setPendingPosition(null); }}
        onConfirm={() => { saveCalculatedPosition(pendingPosition); setPendingPosition(null); }}
      />
    )}
    {showSelectionIntro && (
      <div
        className="selection-intro-overlay"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}
        onClick={e => {
          e.stopPropagation();
          setShowSelectionIntro(false);
          justClosedIntro.current = true;
          overlayDismissAtRef.current = Date.now();
        }}
      >
        <div className="selection-intro-message">
          <div className="selection-intro-rect" />
          <p>
            Marca la posición de la línea de firma para este invitado.<br/>
            {isMobile
              ? 'Tap para continuar; Doble Tap para cancelar el modo selección de posición.'
              : 'Clic para continuar; Clic derecho para cancelar el modo selección de posición.'
            }
          </p>
        </div>
      </div>
    )}

    {
      isMobile
        ? (
      <TransformWrapper
          initialScale={1}
          minScale={0.5}
          maxScale={5}
          centerOnInit={isMobileFull}
          disabled={isSignaturePositionModeActive}
          pinch={{ disabled: isSignaturePositionModeActive }}
          panning={{ disabled: true }}
          wheel={{ disabled: isSignaturePositionModeActive }}
          doubleClick={{ disabled: isSignaturePositionModeActive }}
        >
        <TransformComponent
          wrapperStyle={{ width: '100%', height: '100%', overflow: 'visible' }}
          contentStyle={{ width: '100%' }}
        >
          <Document
            file={cloudPDF}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            options={options}
            loading={<SignatureLoader />}
            noData={<SignatureLoader />}
            error={<div style={{ color: 'white', marginLeft: '20px' }}>Hubo un error al cargar el PDF.</div>}
          >
            {numPages > 0 && (
              <AttachContext.Provider value={{ showButton: showAttachButton, onAttach: onAttachFile, isMobile, bottomPad: (isMobile && watch) ? MOBILE_BOTTOM_PAD : 0 }}>
                <FixedSizeList
                  ref={listRef}
                  height={isMobile ? (mobileListHeight || (isMobileFull ? window.innerHeight : mobileHeight)) : (containerSize.height || window.innerHeight * 0.8)}
                  width={isMobileFull ? window.innerWidth : isMobile ? mobileWidth : (containerSize.width || window.innerWidth)}
                  itemCount={numPages}
                  itemSize={pageItemHeight}
                  innerElementType={InnerList}
                  onItemsRendered={({ visibleStartIndex }) => {
                    // Solo el visor principal (watch=true) es la fuente de verdad de
                    // "página actual". El sidebar de miniaturas (watch=false) también
                    // monta su propio FixedSizeList y, si también despachara aquí,
                    // pelearía con el visor principal por el mismo valor de redux
                    // (causaba que el control de páginas se quedara "trabado").
                    if (watch) {
                      dispatch(setPage(visibleStartIndex + 1));
                    }
                  }}
                  itemData={{
                  isFullScreen,
                  isMobile,
                  watch,
                  readOnly,
                  mobileWidth,
                  defaultWidth,
                  mobileHeight,
                  height,
                  handleScroll,
                  handleMaker,
                  handlePageRenderSuccess,
                  commentsByPage,
                  inputState,
                  setInputState,
                  currentParticipant,
                  userInfo,
                  dispatch,
                  addComment,
                  postCommentApi,
                  documentInfo,
                  signaturePositions,
                  participants,
                  dimenssions,
                  setRef: (index, el) => {
                    if (refDocs.current[index]) {
                      refDocs.current[index].current = el;
                    }
                  },
                  isMobileFull,
                  pendingPosition,
                  setPendingPosition,
                  pendingNodeRef,
                  saveCalculatedPosition,
                  activePage
                }}
                  // overscrollBehaviorY: 'contain' evita que al llegar al inicio/fin de las hojas el
                  // gesto se encadene al navegador (pull-to-refresh del sitio en mobile).
                  style={{ overflow: 'auto', overscrollBehaviorY: 'contain', touchAction: (isSignaturePositionModeActive && !pendingPosition) ? 'none' : 'pan-y' }}
                >
                  {Row}
                </FixedSizeList>
              </AttachContext.Provider>
            )}
          </Document>
        </TransformComponent>
      </TransformWrapper>
          )
        : (
      <Document
        file={cloudPDF}
        onLoadSuccess={onDocumentLoadSuccess}
        onLoadError={onDocumentLoadError}
        options={options}
        loading={<SignatureLoader />}
        noData={<SignatureLoader />}
        error={<div style={{ color: 'white', marginLeft: '20px' }}>Hubo un error al cargar el PDF.</div>}
      >
        {numPages > 0 && containerSize.height > 0 && (
          <AttachContext.Provider value={{ showButton: showAttachButton, onAttach: onAttachFile, isMobile }}>
            <FixedSizeList
              ref={listRef}
              height={containerSize.height}
              width={containerSize.width}
              itemCount={numPages}
              itemSize={pageItemHeight}
              innerElementType={InnerList}
              onItemsRendered={({ visibleStartIndex }) => {
                // Ver comentario equivalente arriba: solo el visor principal actualiza
                // la página actual en redux.
                if (watch) {
                  dispatch(setPage(visibleStartIndex + 1));
                }
              }}
              itemData={{
                isFullScreen,
                isMobile,
                watch,
                readOnly,
                mobileWidth,
                defaultWidth,
                mobileHeight,
                height,
                handleScroll,
                handleMaker,
                handlePageRenderSuccess,
                commentsByPage,
                inputState,
                setInputState,
                currentParticipant,
                userInfo,
                dispatch,
                addComment,
                postCommentApi,
                documentInfo,
                signaturePositions,
                participants,
                dimenssions,
                setRef: (index, el) => {
                  if (refDocs.current[index]) {
                    refDocs.current[index].current = el;
                  }
                },
                isMobileFull,
                pendingPosition,
                setPendingPosition,
                pendingNodeRef,
                saveCalculatedPosition,
                activePage
              }}
            >
              {Row}
            </FixedSizeList>
          </AttachContext.Provider>
        )}
      </Document>
          )}
    {!disableSignatureSelector && cursorPos.visible && !isMobile && (
      <div
        style={{
          position: 'absolute',
          // El cuadro guía es la huella exacta de la firma: su esquina superior izquierda es el cursor,
          // que es el punto que calculatePositionFromEvent toma como esquina de la firma.
          top: cursorPos.y,
          left: cursorPos.x,
          width: `${cursorGeometry.width * cursorScaleX}px`,
          height: `${cursorGeometry.height * cursorScaleY}px`,
          pointerEvents: 'none',
          cursor: 'move'
        }}
      >
        <div
          style={{
            width: '100%',
            height: '100%',
            border: '1px solid #888',
            backgroundColor: 'transparent'
          }}
        />
        <div
          style={{
            position: 'absolute',
            top: '-8px',
            right: '-8px',
            pointerEvents: 'auto'
          }}
        >
          <TbArrowsMove />
        </div>
      </div>
    )}
  </div>
  );
};
export default DocumentPdf;
